import { Link, useNavigate } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Buildings, CaretRight, Pencil } from "@phosphor-icons/react";
import { COMPANY_STATUS_CONFIG } from "@/lib/status-configs";
import type { Company } from "@/types";
import { pluralBs } from "./helpers";

export type StatusFilter = "active" | "suspended";

const SINGULAR_LABEL: Record<StatusFilter, string> = {
  active: "Aktivna",
  suspended: "Suspendirana",
};

function formatDateNumeric(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}.`;
}

function fmtMoney(n: number): string {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function StatusBadge({ company }: { company: Company }) {
  const config = COMPANY_STATUS_CONFIG[company.status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1",
        config.bg,
        config.color,
        config.ring,
      )}
    >
      <span className={cn("size-1.5 rounded-full", config.dot)} />
      {SINGULAR_LABEL[company.status]}
    </span>
  );
}

function CompanyIcon({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-xl bg-primary/10",
        className,
      )}
    >
      <Buildings className="size-[45%] text-primary" />
    </div>
  );
}

function EditButton({
  company,
  onEdit,
  className,
}: {
  company: Company;
  onEdit: (company: Company) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={`Uredi firmu: ${company.name}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onEdit(company);
      }}
      className={cn(
        "inline-flex size-7 items-center justify-center rounded-lg text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground",
        className,
      )}
    >
      <Pencil className="size-4" />
    </button>
  );
}

/* ─── Desktop table + mobile cards ─── */

export function CompaniesTable({
  companies,
  onEdit,
}: {
  companies: Company[];
  onEdit: (company: Company) => void;
}) {
  const navigate = useNavigate();

  const openDetail = (company: Company) =>
    navigate({
      to: "/dashboard/admin/companies/$companyId",
      params: { companyId: company.id },
    });

  return (
    <>
      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm lg:block">
        <table className="w-full table-fixed text-[13px]">
          <colgroup>
            <col />
            <col className="w-[110px]" />
            <col className="w-[104px]" />
            <col className="w-[120px]" />
            <col className="w-[108px]" />
            <col className="w-[136px]" />
            <col className="w-[88px]" />
          </colgroup>
          <thead>
            <tr className="bg-muted/40">
              <Th>Firma</Th>
              <Th>Plan</Th>
              <Th align="right">Zaposleni</Th>
              <Th align="right">Naplata</Th>
              <Th align="right">Kreirana</Th>
              <Th align="right">Status</Th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {companies.map((company, i) => (
              <tr
                key={company.id}
                onClick={() => openDetail(company)}
                className={cn(
                  "group cursor-pointer transition-colors hover:bg-primary/[0.03]",
                  i !== companies.length - 1 && "border-b border-border/40",
                )}
              >
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-3">
                    <CompanyIcon className="size-9" />
                    <div className="min-w-0">
                      <div className="truncate font-medium leading-tight text-foreground">
                        {company.name}
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                        {company.billingEmail}
                      </div>
                      <AttentionLine company={company} />
                    </div>
                  </div>
                </td>
                <td className="truncate px-3 py-3.5 text-muted-foreground">
                  {company.planType}
                </td>
                <td
                  className="whitespace-nowrap px-3 py-3.5 text-right tabular-nums text-foreground"
                  title={`Supervizori: ${company.supervisorCount}`}
                >
                  {company.employeeCount}
                  <span className="text-[11px] text-muted-foreground">
                    /{company.maxEmployees}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-right tabular-nums text-foreground">
                  {company.billingModel === "invoice" ? (
                    <span className="text-muted-foreground">Faktura</span>
                  ) : (
                    <>
                      {fmtMoney(company.creditBalance ?? 0)}
                      <span className="ml-0.5 text-[11px] text-muted-foreground">KM</span>
                    </>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-right tabular-nums text-muted-foreground">
                  {formatDateNumeric(company.createdAt)}
                </td>
                <td className="px-3 py-3.5 text-right">
                  <StatusBadge company={company} />
                </td>
                <td className="py-3.5 pl-1 pr-4 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <EditButton company={company} onEdit={onEdit} />
                    <Link
                      to="/dashboard/admin/companies/$companyId"
                      params={{ companyId: company.id }}
                      aria-label={`Detalji: ${company.name}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex size-7 items-center justify-center rounded-lg text-muted-foreground/50 transition-colors group-hover:text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <CaretRight className="size-4" />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:hidden">
        {companies.map((company) => (
          <Link
            key={company.id}
            to="/dashboard/admin/companies/$companyId"
            params={{ companyId: company.id }}
            className="group rounded-2xl border border-border/60 bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <CompanyIcon className="size-11" />
                <div className="min-w-0">
                  <div
                    className="truncate text-sm font-semibold text-foreground"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {company.name}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {company.billingEmail}
                  </div>
                  <AttentionLine company={company} />
                </div>
              </div>
              <StatusBadge company={company} />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border/40 pt-3 text-xs">
              <MobileCell
                label="Zaposleni"
                value={`${company.employeeCount}/${company.maxEmployees}`}
              />
              <MobileCell
                label="Naplata"
                value={company.billingModel === "invoice" ? "Faktura" : `${fmtMoney(company.creditBalance ?? 0)} KM`}
              />
              <MobileCell label="Plan" value={company.planType} muted />
              <MobileCell
                label="Supervizori"
                value={String(company.supervisorCount)}
                muted
              />
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-border/40 pt-3 text-xs text-muted-foreground">
              <span className="tabular-nums">
                {formatDateNumeric(company.createdAt)}
              </span>
              <span className="inline-flex items-center gap-1">
                <EditButton company={company} onEdit={onEdit} />
                <CaretRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}

function MobileCell({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 truncate tabular-nums",
          muted ? "text-muted-foreground" : "font-semibold text-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={cn(
        "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground first:px-4",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {children}
    </th>
  );
}

/** Šta čeka admina: otvoreni zahtjevi supervizora, nacrti faktura, dospjele fakture. */
function AttentionLine({ company }: { company: Company }) {
  const parts: string[] = [];
  const n = (count: number | undefined, one: string, few: string, other: string) => {
    if (count) parts.push(pluralBs(count, one, few, other));
  };
  n(company.openRequestCount, "zahtjev", "zahtjeva", "zahtjeva");
  n(company.draftInvoiceCount, "nacrt fakture", "nacrta fakture", "nacrta faktura");
  n(company.overdueInvoiceCount, "dospjela faktura", "dospjele fakture", "dospjelih faktura");
  if (parts.length === 0) return null;
  return <div className="mt-0.5 truncate text-[11px] font-medium text-amber-700">{parts.join(" · ")}</div>;
}
